import { pickedPointAtom } from '@/atoms';
import { clipBoxStateAtom, viewerEvents } from '@/atoms/viewer.atom';
import { execute } from '@/graphql/execute';
import { useFeatureCheck } from '@/hooks/useFeature';
import { useOrganizations } from '@/hooks/useOrganizations';
import { updateProjectMutation } from '@/mutations/project.mutation';
import { getProjectQuery } from '@/queries/project.query';
import { CenterFocus } from '@mynaui/icons-react';
import { DropdownMenu } from '@radix-ui/react-dropdown-menu';
import { useAtom, useSetAtom } from 'jotai';
import { Box, Copy, Crosshair } from 'lucide-react';
import { FunctionComponent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

export const ViewerContextMenu: FunctionComponent = () => {
  const [pickedPoint, setPickedPoint] = useAtom(pickedPointAtom);
  const { hasRole } = useOrganizations();
  const { projectId } = useParams();
  const { t } = useTranslation('components');
  const setClipBoxState = useSetAtom(clipBoxStateAtom(projectId!));
  const checkFeature = useFeatureCheck();

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      // Only prevent default if we have a picked point
      if (pickedPoint?.position) {
        e.preventDefault();
      }
    };

    document.body.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.body.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [pickedPoint]);

  const handleCopyCoordinates = () => {
    if (!pickedPoint?.position) {
      toast.error(t('pointCloudContextMenu.noPointSelected'));
      return;
    }

    const position = pickedPoint.position;
    const coordinates = `${position.x.toFixed(3)}, ${position.y.toFixed(3)}, ${position.z.toFixed(3)}`;

    console.log(pickedPoint);

    navigator.clipboard
      .writeText(coordinates)
      .then(() => {
        toast.success(t('pointCloudContextMenu.coordinatesCopied'));
      })
      .catch(() => {
        toast.error(t('pointCloudContextMenu.failedCopyCoordinates'));
      });
  };

  const _handleInitialPosition = async () => {
    const projectData = await execute(getProjectQuery, { id: projectId! });
    if (!projectData) {
      toast.error(t('pointCloudContextMenu.projectNotFound'));
      return;
    }

    const initialPosition = projectData.project.initialPosition || {};
    if (!initialPosition) throw new Error('Initial position not found');
    initialPosition.coords = [pickedPoint?.position?.x, pickedPoint?.position?.y, pickedPoint?.position?.z];
    await execute(updateProjectMutation, {
      input: {
        id: projectId!,
        initialPosition,
        initialAngle: parseInt(pickedPoint?.cameraAngle.toFixed(0) || '0', 10),
      },
    });
  };

  const _handleSetInitialPosition = async () => {
    toast.promise(_handleInitialPosition, {
      loading: t('pointCloudContextMenu.settingInitialPosition'),
      success: t('pointCloudContextMenu.initialPositionSet'),
      error: t('pointCloudContextMenu.failedSetInitialPosition'),
    });
  };

  const handleSetCenter = () => {
    if (!pickedPoint?.position) return;
    viewerEvents.emit('setCenterTarget', {
      x: pickedPoint.position.x,
      y: pickedPoint.position.y,
      z: pickedPoint.position.z,
    });
  };

  const handleStartClipBox = () => {
    if (!pickedPoint?.position) return;
    const gate = checkFeature('viewer.clipBox');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    const { x, y, z } = pickedPoint.position;
    setClipBoxState((prev) => ({
      ...prev,
      enabled: true,
      center: [x, y, z],
      halfSize: [5, 5, 5],
      rotation: [0, 0, 0, 1],
      interactionMode: 'EDIT',
      clipMode: prev.clipMode === 'NONE' ? 'HIGHLIGHT_INSIDE' : prev.clipMode,
    }));
  };

  if (!pickedPoint?.position) return null;
  // Pick the deviation attribute off the picked point, if present. The
  // attribute key is whatever the deviation worker named the LAS extra dim
  // ('Deviation' by convention). The picker emits raw float values in the
  // same units the script wrote (metres). We auto-pick a more readable
  // unit (mm < 5 mm, cm < 0.5 m, m otherwise) so survey-scale deviations
  // don't read as "0.005 m".
  const devAttr = pickedPoint.attributes
    ? (pickedPoint.attributes['Deviation'] ??
        pickedPoint.attributes['deviation'] ??
        pickedPoint.attributes['DEVIATION'])
    : undefined;
  const devValue = typeof devAttr === 'number' ? devAttr : undefined;
  const formatDeviation = (m: number): string => {
    const a = Math.abs(m);
    if (a < 0.005) return `${(m * 1000).toFixed(2)} mm`;
    if (a < 0.5) return `${(m * 100).toFixed(2)} cm`;
    return `${m.toFixed(3)} m`;
  };
  return (
    <DropdownMenu
      open
      onOpenChange={(open) => {
        if (!open) setPickedPoint(null);
      }}>
      <DropdownMenuContent
        className='w-64'
        style={{ left: pickedPoint?.screenX, top: pickedPoint?.screenY, position: 'fixed', zIndex: 1000 }}>
        <DropdownMenuLabel className='font-normal'>
          <div className='text-[10px] uppercase tracking-wide text-slate-500 dark:text-stone-400'>
            {t('pointCloudContextMenu.position', { defaultValue: 'Position' })}
          </div>
          <div className='font-mono text-xs text-slate-700 dark:text-stone-200'>
            {pickedPoint.position.x.toFixed(3)}, {pickedPoint.position.y.toFixed(3)},{' '}
            {pickedPoint.position.z.toFixed(3)}
          </div>
          {devValue !== undefined && Number.isFinite(devValue) && (
            <div className='mt-1.5'>
              <div className='text-[10px] uppercase tracking-wide text-slate-500 dark:text-stone-400'>
                {t('pointCloudContextMenu.deviation', { defaultValue: 'Deviation' })}
              </div>
              <div className='font-mono text-xs text-slate-700 dark:text-stone-200'>
                {devValue >= 0 ? '+' : ''}
                {formatDeviation(devValue)}
              </div>
            </div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={handleCopyCoordinates} disabled={!pickedPoint?.position}>
            <Copy className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
            {t('pointCloudContextMenu.copyCoordinates')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleSetCenter} disabled={!pickedPoint?.position}>
            <Crosshair className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
            {t('pointCloudContextMenu.setAsCenter')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleStartClipBox} disabled={!pickedPoint?.position}>
            <Box className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
            {t('pointCloudContextMenu.startClipBoxHere')}
          </DropdownMenuItem>
          {hasRole('EDITOR') && (
            <DropdownMenuItem onClick={_handleSetInitialPosition} disabled={!pickedPoint?.position}>
              <CenterFocus className='size-4 stroke-2 text-slate-700 dark:text-stone-100' />
              {t('pointCloudContextMenu.setAsInitialPosition')}
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
